import {
  Spinner,
  Flex,
  Tooltip,
  Text,
  Box,
  useColorMode,
  ColorMode,
  Button,
} from '@chakra-ui/react';
import { Composition } from '@texas/api/endpoints/compositionApi';
import { Icons, defaultIconSize } from '@texas/components/shared/Icons';
import { fadeInScaleAnimation } from '@texas/resources/animations/animations';
import { navLinkOffsetStyle } from '@texas/resources/styles';
import { useTranslation } from 'react-i18next';
import options from '@assets/article/options.jpeg';
import { TopRightContainerComponent } from '@texas/components/shared/TopRightContainerComponent';
import { WarningComponent } from '@texas/components/shared/WarningComponent';

interface Mode {
  hideCreate?: boolean;
}

type View = 'default' | 'list';

interface Group {
  id: number;
  fileId: number | null;
  version: number;
  note: string | null;
  compositions: Composition[];
  isReady: boolean;
}

export function OptionsList({
  groups,
  groupsLoading,
  isCreating,
  onCreateClick,
  selectedOption,
  onClick,
  mode,
  view,
}: {
  groups: Group[] | undefined;
  groupsLoading: boolean;
  isCreating: boolean;
  onCreateClick: () => void;
  selectedOption: number | null;
  onClick: (id: number) => void;
  mode?: Mode;
  view?: View;
}) {
  const { colorMode } = useColorMode();

  return (
    <>
      {groupsLoading ? (
        <Spinner />
      ) : (
        <>
          {groups?.length === 0 && !isCreating && !mode?.hideCreate && (
            <NewOptionCard onClick={onCreateClick} />
          )}
          {groups?.map((x) => {
            if (view === 'list') {
              return (
                <ListItem
                  key={x.id}
                  group={x}
                  selectedOption={selectedOption}
                  onClick={onClick}
                  colorMode={colorMode}
                />
              );
            } else {
              return (
                <Card
                  key={x.id}
                  group={x}
                  selectedOption={selectedOption}
                  colorMode={colorMode}
                  onClick={onClick}
                />
              );
            }
          })}
        </>
      )}
    </>
  );
}

function ListItem({
  group,
  selectedOption,
  onClick,
  colorMode,
}: {
  group: Group;
  selectedOption: number | null;
  onClick: (id: number) => void;
  colorMode: ColorMode;
}) {
  const { t } = useTranslation();
  return (
    <TopRightContainerComponent
      bar={
        <>
          {group.isReady ? (
            <Tooltip label={t('compositionGroup.option.ready')}>
              <Icons.CheckCircle
                bg="texas.bg.800"
                borderRadius="full"
                color="texas.sand.50"
                _light={{
                  color: 'texas.burntSienna.100',
                }}
                boxSize={defaultIconSize}
              />
            </Tooltip>
          ) : null}
        </>
      }
    >
      <Button
        w="100%"
        maxW="250px"
        whiteSpace="break-spaces"
        height="auto"
        padding="12px 4px"
        onClick={() => onClick(group.id)}
        {...navLinkOffsetStyle(selectedOption === group.id, 1.02, colorMode)}
        {...(selectedOption === group.id
          ? { bg: 'gray.600', _light: { bg: 'gray.200' } }
          : {})}
      >
        {group.note
          ? group.note
          : t('compositionGroup.option.optionNr', {
              nr: group.version,
            })}
      </Button>
    </TopRightContainerComponent>
  );
}

function Card({
  group,
  selectedOption,
  colorMode,
  onClick,
}: {
  group: Group;
  selectedOption: number | null;
  colorMode: ColorMode;
  onClick: (id: number) => void;
}) {
  const { t } = useTranslation();

  return (
    <Box
      animation={fadeInScaleAnimation()}
      cursor="pointer"
      borderRadius="md"
      _hover={{
        transform: 'scale(1.02)',
      }}
      {...navLinkOffsetStyle(selectedOption === group.id, 1.02, colorMode)}
    >
      <TopRightContainerComponent
        bar={
          <>
            {group.isReady ? (
              <Tooltip label={t('compositionGroup.option.ready')}>
                <Icons.CheckCircle
                  bg="texas.bg.800"
                  borderRadius="full"
                  color="texas.sand.50"
                  _light={{
                    color: 'texas.burntSienna.100',
                  }}
                  boxSize={defaultIconSize}
                />
              </Tooltip>
            ) : null}
          </>
        }
      >
        <Flex
          background={`linear-gradient(to top, #00000080 20%, transparent), url(${options})`}
          _light={{
            background: `linear-gradient(to top, #FFFFFF80 20%, transparent), url(${options})`,
            ...(group.isReady
              ? {
                  borderColor: 'texas.burntSienna.100',
                }
              : null),
          }}
          backgroundSize="cover"
          w="140px"
          h="200px"
          borderRadius="md"
          padding={2}
          justifyContent="end"
          flexDir="column"
          onClick={() => onClick(group.id)}
          {...(group.isReady
            ? {
                border: '2px solid',
                borderColor: 'texas.sand.50',
              }
            : null)}
        >
          <Flex align="center">
            {group.fileId === null ? (
              <WarningComponent warning={t('compositionGroup.missingLayout')}>
                <Icons.PencilRuler boxSize={defaultIconSize} />
              </WarningComponent>
            ) : (
              <Icons.PencilRuler boxSize={defaultIconSize} />
            )}
            {group.compositions.some((c) => c.fileId) && (
              <Tooltip
                label={t('fileBrowser.images', {
                  count: group.compositions.filter((c) => c.fileId).length,
                })}
              >
                <Icons.ImageOutline boxSize={defaultIconSize} ml={2} />
              </Tooltip>
            )}
          </Flex>
          <Text fontWeight="bold">
            {group.note
              ? group.note
              : t('compositionGroup.option.optionNr', {
                  nr: group.version,
                })}
          </Text>
        </Flex>
      </TopRightContainerComponent>
    </Box>
  );
}

function NewOptionCard({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <Flex
      w="140px"
      h="200px"
      align="center"
      justify="center"
      border="2px dashed"
      borderColor="texas.bg.700"
      textAlign="center"
      borderRadius="md"
      cursor="pointer"
      transition="transform 200ms ease"
      _hover={{
        transform: 'scale(1.02)',
        borderColor: 'white',
        _light: { borderColor: 'black' },
      }}
      _light={{
        borderColor: 'gray.200',
      }}
      onClick={onClick}
    >
      <Text>{t('compositionGroup.option.createFirst')}</Text>
    </Flex>
  );
}
